import React from 'react';
import { Municipality, getFeesForYear, getYearOverYearChange, filterFees, DEFAULT_ENABLED_CATEGORIES } from '@/lib/data';

interface KommuneWidgetProps {
  municipalities: Municipality[];
  year: number;
  enabledCategories?: string[];
  dark?: boolean;
}

const getChangeClass = (change: number, dark?: boolean) => {
  if (change > 0) return dark ? 'text-amber-400' : 'text-amber-600';
  if (change < 0) return dark ? 'text-slate-400' : 'text-slate-600';
  return dark ? 'text-slate-500' : 'text-warmGray';
};

export const KommuneWidget: React.FC<KommuneWidgetProps> = ({ municipalities, year, enabledCategories = DEFAULT_ENABLED_CATEGORIES, dark }) => {
  const data = municipalities
    .map(m => ({
      m,
      total: filterFees(getFeesForYear(m, year), enabledCategories).reduce((s, f) => s + f.amount, 0),
      change: getYearOverYearChange(m, year, enabledCategories),
    }))
    .filter(x => x.total > 0)
    .sort((a, b) => b.total - a.total);

  const maxTotal = data.length > 0 ? Math.max(...data.map(x => x.total)) : 1;

  return (
    <div className="space-y-3">
      {data.map(({ m, total, change }) => (
        <div key={m.id}>
          <div className="flex justify-between items-center text-xs mb-1 gap-2">
            <span className={dark ? 'text-slate-300' : 'text-deepCharcoal'}>{m.name}</span>
            <span className="flex items-center gap-1.5 shrink-0">
              {change !== null && (
                <span className={`tabular-nums text-[10px] ${getChangeClass(change, dark)}`}>
                  {change > 0 ? '+' : ''}{change.toFixed(1)}%
                </span>
              )}
              <span className={`tabular-nums ${dark ? 'text-slate-400' : 'text-nordicBlue'}`}>
                {total.toLocaleString('nb-NO')} kr
              </span>
            </span>
          </div>
          <div className={`h-1.5 rounded-full overflow-hidden ${dark ? 'bg-slate-700' : 'bg-gray-100'}`}>
            <div
              className={`h-full rounded-full ${dark ? 'bg-slate-500' : 'bg-nordicBlue/70'}`}
              style={{ width: `${Math.min(100, (total / maxTotal) * 100)}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};
